/**
 * Creates the floor-plan editor state — called ONCE by the editor's root
 * component, which provides the result through FloorPlanEditorStateContext.
 * Everything below the root reads it with useFloorPlanEditorState(); nothing
 * passes it around.
 *
 * Every editor-model update REPLACES `st.shape` (and `draftShape`) with a
 * pure, structurally-shared room from the floor-plan edit catalog — nothing
 * mutates a committed room. So a state update is a shallow copy of the
 * container, committed-change detection is reference identity, and untouched
 * subtrees keep their object identity across snapshots (memoization-ready).
 */

import { defaultShape, FloorPlanRoom, sameSavedRoom } from '@kaiizen/kai-floor-plan';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createEditorState,
  DEFAULT_FLOOR_TYPE_OPTIONS,
  EditorMutableState,
  FloorTypeOption,
  type InputKind,
  select as modelSelect,
  Selection,
  selectionForTransition,
} from '../editor-model';
import { FloorPlanEditorMode, FloorPlanEditorState } from './FloorPlanEditorState';
import { makeFloorPlanEdits } from './floorPlanEdits';
import { useVersionHistory } from './useVersionHistory';

/**
 * Shallow-copy the container for the next snapshot. Rooms (`shape`,
 * `draftShape`) and `drag` are replaced-not-mutated by the editor-model, so
 * their references are shared; only `viewport` is still edited in place by
 * pan/zoom and needs its own copy.
 */
function cloneEditorState(editorState: EditorMutableState): EditorMutableState {
  return {
    ...editorState,
    viewport: { scale: editorState.viewport.scale, offset: { ...editorState.viewport.offset } },
  };
}

/**
 * The updater `mutate` runs is pure, since StrictMode runs it twice; commit
 * side effects live in the version history's effect. Each committed room (not
 * an in-flight drag preview) is a version, and the room is unsaved while it
 * would save differently from the saved version (`sameSavedRoom`). The host's
 * `floorTypes`, `readOnly` and `snapping` props land in the next snapshot
 * without touching the room. `inputKind` is the host's, set once: mouse or
 * finger. `select` goes through the model's select, so its
 * side effects (gap clearing, free-element template capture) apply to 3D
 * picks too. `edit` and `commit` close over the stable `mutate`, so both are
 * built once.
 */
export function useCreateFloorPlanEditorState(
  initialShape?: FloorPlanRoom,
  floorTypeOptions?: FloorTypeOption[],
  readOnly?: boolean,
  snapping = true,
  inputKind: InputKind = 'pointer',
): FloorPlanEditorState {
  const [editorMutableState, setEditorMutableState] = useState<EditorMutableState>(
    () => createEditorState(initialShape ?? defaultShape(), floorTypeOptions, readOnly, snapping, inputKind),
  );
  const editorMutableStateRef = useRef(editorMutableState);
  editorMutableStateRef.current = editorMutableState;

  const mutate = useCallback((mutateEditorState: (editorState: EditorMutableState) => void) => {
    setEditorMutableState(previousEditorState => {
      const nextEditorState = cloneEditorState(previousEditorState);
      mutateEditorState(nextEditorState);
      return nextEditorState;
    });
  }, []);

  const history = useVersionHistory(editorMutableState.shape, { committed: !editorMutableState.draftShape, same: sameSavedRoom });

  useEffect(() => {
    const nextFloorTypeOptions = floorTypeOptions ?? DEFAULT_FLOOR_TYPE_OPTIONS;
    if (nextFloorTypeOptions !== editorMutableStateRef.current.floorTypeOptions) {
      mutate(editorState => {
        editorState.floorTypeOptions = nextFloorTypeOptions;
      });
    }
  }, [floorTypeOptions, mutate]);

  useEffect(() => {
    const nextReadOnly = readOnly ?? false;
    if (nextReadOnly !== (editorMutableStateRef.current.readOnly ?? false)) {
      mutate(editorState => {
        editorState.readOnly = nextReadOnly;
      });
    }
  }, [readOnly, mutate]);

  useEffect(() => {
    if (snapping !== (editorMutableStateRef.current.snapping ?? false)) {
      mutate(editorState => {
        editorState.snapping = snapping;
      });
    }
  }, [snapping, mutate]);

  const setShape = useCallback((shape: FloorPlanRoom) => {
    mutate(editorState => {
      editorState.shape = shape;
      editorState.selection = { kind: 'none' };
      editorState.draftShape = null;
      editorState.drag = null;
    });
  }, [mutate]);

  const select = useCallback((selection: Selection) => mutate(editorState => modelSelect(editorState, selection)), [mutate]);

  const setMode = useCallback((mode: FloorPlanEditorMode) => {
    mutate(editorState => {
      editorState.mode = mode;
    });
  }, [mutate]);

  /**
   * Apply an undo/redo target: like setShape, but the stacks are
   * pre-positioned and the selection is the diffed changed part (routed
   * through the model's select for its side effects).
   */
  const restore = useCallback((shape: FloorPlanRoom, selection: Selection) => {
    mutate(editorState => {
      editorState.shape = shape;
      editorState.draftShape = null;
      editorState.drag = null;
      editorState.editingKey = null;
      editorState.addMenu = null;
      modelSelect(editorState, selection);
    });
  }, [mutate]);

  /**
   * Show an undo or redo step's room, selecting what the step changed as it
   * exists in that room. Mid-drag the draft owns the geometry, so stepping
   * the history under it would be clobbered by the drag's own commit.
   */
  const { undo: undoStep, redo: redoStep, canUndo, canRedo, isDirty, markSaved } = history;
  const stepTo = useCallback((takeStep: typeof undoStep) => {
    if (editorMutableStateRef.current.drag || editorMutableStateRef.current.draftShape) return;
    const step = takeStep();
    if (step) restore(step.to, selectionForTransition(step.from, step.to));
  }, [restore]);
  const undo = useCallback(() => stepTo(undoStep), [stepTo, undoStep]);
  const redo = useCallback(() => stepTo(redoStep), [stepTo, redoStep]);

  const edit = useMemo(() => makeFloorPlanEdits(mutate), [mutate]);
  const commit = useCallback(
    (nextFloorPlan: FloorPlanRoom) =>
      mutate(editorState => {
        editorState.shape = nextFloorPlan;
      }),
    [mutate],
  );

  return useMemo(
    () => ({
      st: editorMutableState,
      mutate,
      setShape,
      select,
      setMode,
      undo,
      redo,
      canUndo,
      canRedo,
      isDirty,
      markSaved,
      stRef: editorMutableStateRef,
      floorPlan: editorMutableState.shape,
      edit,
      commit,
    }),
    [editorMutableState, mutate, setShape, select, setMode, undo, redo, canUndo, canRedo, isDirty, markSaved, edit, commit],
  );
}
